/**
 * STAGING-LAUNCH-READINESS-002-R2-R8-R2 — single render of registry order actions.
 * CUSTOMER-COMMS-140-R1 — one-column mobile + semantic role tokens.
 */
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  actionLabelEn,
  actionLabelUa,
  actionRole,
  type CustomerOrderActionId,
  type CustomerOrderActionsResult,
} from "./customer-order-actions-registry-v2.js";

export type CustomerOrderActionHandlers = {
  onContinuePayment?: () => void;
  onSendReceipt?: () => void;
  onViewPaymentDetails?: () => void;
  onViewDelivery?: () => void;
  onCheckPaymentStatus?: () => void;
  onChangePaymentMethod?: () => void;
  onAbandonPayment?: () => void;
  onCancelOrder?: () => void;
  onContactManager?: () => void;
  onRequestReturn?: () => void;
  onRequestCancellation?: () => void;
  onReturnToOrder?: () => void;
  onToCatalog?: () => void;
  busy?: Partial<Record<CustomerOrderActionId, boolean>>;
};

const TEST_IDS: Record<CustomerOrderActionId, string> = {
  continue_payment: "sv2-continue-payment-btn",
  send_receipt: "sv2-send-receipt-action-btn",
  view_payment_details: "sv2-view-payment-details-btn",
  view_delivery: "sv2-view-delivery-btn",
  check_payment_status: "sv2-check-payment-status-btn",
  change_payment_method: "sv2-change-payment-method-btn",
  abandon_payment: "sv2-abandon-payment-btn",
  cancel_order: "sv2-cancel-order-btn",
  contact_manager: "sv2-order-contact-manager",
  request_return: "sv2-request-refund-btn",
  request_cancellation: "sv2-request-cancellation-btn",
  return_to_order: "sv2-return-to-order-btn",
  to_catalog: "sv2-order-to-catalog-btn",
};

function roleClass(id: CustomerOrderActionId): string {
  const role = actionRole(id);
  switch (role) {
    case "primary":
      return "sv2-cta sv2-action-primary";
    case "accent":
      return "sv2-cta sv2-action-accent";
    case "secondary":
      return "sv2-cta sv2-action-secondary";
    case "tertiary":
      return "sv2-cta sv2-action-tertiary";
    case "danger":
      return "sv2-cta sv2-action-danger";
    default:
      return "sv2-cta sv2-action-secondary";
  }
}

export function CustomerOrderActionsBarV2({
  lang,
  plan,
  handlers,
  testId = "sv2-canonical-order-actions",
}: {
  lang: StoreV2Lang;
  plan: CustomerOrderActionsResult;
  handlers: CustomerOrderActionHandlers;
  testId?: string;
}) {
  const en = lang === "en";
  if (!plan.actions.length) return null;

  const run = (id: CustomerOrderActionId) => {
    switch (id) {
      case "continue_payment":
        handlers.onContinuePayment?.();
        break;
      case "send_receipt":
        handlers.onSendReceipt?.();
        break;
      case "view_payment_details":
        handlers.onViewPaymentDetails?.();
        break;
      case "view_delivery":
        handlers.onViewDelivery?.();
        break;
      case "check_payment_status":
        handlers.onCheckPaymentStatus?.();
        break;
      case "change_payment_method":
        handlers.onChangePaymentMethod?.();
        break;
      case "abandon_payment":
        handlers.onAbandonPayment?.();
        break;
      case "cancel_order":
        handlers.onCancelOrder?.();
        break;
      case "contact_manager":
        handlers.onContactManager?.();
        break;
      case "request_return":
        handlers.onRequestReturn?.();
        break;
      case "request_cancellation":
        handlers.onRequestCancellation?.();
        break;
      case "return_to_order":
        handlers.onReturnToOrder?.();
        break;
      case "to_catalog":
        handlers.onToCatalog?.();
        break;
    }
  };

  return (
    <div
      className="sv2-order-detail-actions sv2-canonical-order-actions sv2-postorder-actions"
      data-testid={testId}
      data-owns-manager={plan.ownsManagerCta ? "1" : "0"}
      data-action-count={plan.actions.length}
    >
      {plan.actions.map((id) => {
        const busy = handlers.busy?.[id] === true;
        return (
          <button
            key={id}
            type="button"
            className={roleClass(id)}
            data-testid={TEST_IDS[id]}
            data-action-id={id}
            data-action-role={actionRole(id)}
            disabled={busy}
            onClick={() => run(id)}
          >
            {busy
              ? en
                ? "Working…"
                : "Зачекайте…"
              : en
                ? actionLabelEn(id)
                : actionLabelUa(id)}
          </button>
        );
      })}
    </div>
  );
}
